import { Country, MileageUnit, PaperworkType } from '../gql/generated/graphql';

import { Option } from '../components/shared/types';

export const DEFAULT_COUNTRY: Country = Country.Us;

export const COUNTRY_LABELS: Record<Country, string> = {
  [Country.Us]: 'United States',
  [Country.Ca]: 'Canada',
};

export const COUNTRY_OPTIONS: Option[] = [Country.Us, Country.Ca].map((value) => ({
  value,
  label: COUNTRY_LABELS[value],
}));

export const countryFromString = (value?: string | null): Country =>
  value === Country.Ca ? Country.Ca : Country.Us;

export const isCanada = (country?: string | null): boolean => country === Country.Ca;

type DealCountryFields = {
  country?: string | null;
  customer?: { address?: { country?: string | null } | null } | null;
};

// The country a Formik deal is valued and addressed in. The deal's own country is fixed at
// creation and the address form writes it alongside the address country, so it wins; the
// address country covers an estimate created before the field existed.
export const dealCountryFor = (values: DealCountryFields): Country =>
  countryFromString(values.country ?? values.customer?.address?.country);

type CountryAddressConfig = {
  regionLabel: string;
  postalCodeLabel: string;
  postalCodeMask: string;
};

// react-input-mask format: 9 = digit, a = letter.
const COUNTRY_ADDRESS_CONFIG: Record<Country, CountryAddressConfig> = {
  [Country.Us]: {
    regionLabel: 'State',
    postalCodeLabel: 'Zip',
    postalCodeMask: '99999',
  },
  [Country.Ca]: {
    regionLabel: 'Province',
    postalCodeLabel: 'Postal Code',
    postalCodeMask: 'a9a 9a9',
  },
};

export const addressConfigForCountry = (country?: string | null): CountryAddressConfig =>
  COUNTRY_ADDRESS_CONFIG[countryFromString(country)];

type CountryRules = {
  hasCounty: boolean;
  requiresSsn: boolean;
  supportsZipLookup: boolean;
  supportsPlateLookup: boolean;
  paperworkTypes: readonly PaperworkType[];
};

const COUNTRY_RULES: Record<Country, CountryRules> = {
  [Country.Us]: {
    hasCounty: true,
    requiresSsn: true,
    supportsZipLookup: true,
    supportsPlateLookup: true,
    paperworkTypes: Object.values(PaperworkType),
  },
  [Country.Ca]: {
    hasCounty: false,
    requiresSsn: false,
    supportsZipLookup: false,
    supportsPlateLookup: false,
    paperworkTypes: [PaperworkType.Adobe],
  },
};

export const countryRulesFor = (country?: string | null): CountryRules =>
  COUNTRY_RULES[countryFromString(country)];

export const defaultMileageUnitForCountry = (country?: string | null): MileageUnit =>
  isCanada(country) ? MileageUnit.Kilometers : MileageUnit.Miles;

type ValuationLabels = {
  lendingLabel: string;
  retailLabel: string;
  // JD Power values exist for US vehicles only.
  showsJdPower: boolean;
};

// Who values the vehicle: KBB and JD Power in the US, Canadian Black Book in Canada. The
// two book value columns are shared; only the label and the JD Power pair change.
export const valuationLabelsFor = (country?: string | null): ValuationLabels =>
  isCanada(country)
    ? {
        lendingLabel: 'CBB Lending',
        retailLabel: 'CBB Retail',
        showsJdPower: false,
      }
    : {
        lendingLabel: 'KBB Lending',
        retailLabel: 'KBB Retail',
        showsJdPower: true,
      };
